







































































import ConfirmModal from '@/components/common/ConfirmModal.vue';
import ResponsiveImage from '@/components/common/Image.vue';
import RichTextDisplay from '@/components/common/RichTextDisplay.vue';
import RichTextEditor from '@/components/form/RichText.vue';
import DeleteBlogPost from '@/graphql/mutations/deleteBlogPost.gql';
import UpdateBlogPost from '@/graphql/mutations/updateBlogPost.gql';
import BlogPostQuery from '@/graphql/queries/blogPost.gql';
import { API_HOST, getAuthHeader, request } from '@/graphql/request';
import { Component, Vue, Watch } from 'vue-property-decorator';

interface IPost {
  id: string;
  name: string;
  author: string;
  text: string;
  summary?: string;
  createdAt: Date;
  updatedAt: Date;
  published: boolean;
  hero?: {
    url: string;
  };
}

@Component({
  components: {
    RichTextDisplay,
    RichTextEditor,
    ConfirmModal,
    ResponsiveImage,
  },
})
export default class Blog extends Vue {
  // State fields
  public id!: string;
  public post: IPost | null = null;
  public readTime: string = '∞ min';
  public isAdmin = false;
  public isEditing = false;
  public errorMessage: string | null = null;

  // Editing mode variables
  public editName?: string;
  public editText?: string;
  public editHero: any = null;
  public editThumbnail: any = null;

  // Modal controls
  public showModal: null | 'delete' | 'save' | 'publish' = null;
  public loading: null | string = null;

  public mounted() {
    this.id = this.$route.params.id;
    this.isEditing = !!this.$route.query.editing;

    this.loadBlogPost();
  }

  public loadBlogPost() {
    request(BlogPostQuery, { id: this.id })
      .then(response => {
        if (!response || !response.data) {
          console.error('No posts returned');
          return;
        }

        this.post = response.data.blogPost;
        this.isAdmin =
          response.data.user && response.data.user.role === 'admin';

        this.editName = response.data.blogPost.name;
        this.editText = response.data.blogPost.text;
      })
      .catch(console.error);
  }

  public updated() {
    this.updateReadTime();
  }

  public startEditing() {
    this.isEditing = true;
    this.$router.replace({ query: { editing: 'true' } });
  }

  public stopEditing() {
    this.isEditing = false;
    this.$router.replace({ query: {} });
  }

  public savePost() {
    this.loading = 'Saving post...';
    this.showModal = null;
    this.errorMessage = null;
    if (!this.post) {
      return;
    }

    const fields: any = {};
    if (this.editName && this.editName !== this.post.name) {
      fields.name = this.editName;
    }

    if (this.editText && this.editText !== this.post.text) {
      fields.text = this.editText;
    }

    this.updatePost(this.id, fields)
      .then(() => {
        if (this.editHero || this.editThumbnail) {
          return this.uploadPhotos({
            hero: this.editHero,
            thumbnail: this.editThumbnail,
          });
        }
      })
      .catch(e => this.displayErrors(e))
      .then(() => (this.loading = null));
  }

  public togglePostPublished() {
    this.loading = 'Changing publish status...';
    this.showModal = null;
    this.errorMessage = null;
    if (!this.post) {
      return;
    }

    this.updatePost(this.id, { published: !this.post.published })
      .catch(e => this.displayErrors(e))
      .then(() => (this.loading = null));
  }

  public deletePost() {
    this.loading = 'Deleting post...';
    this.showModal = null;
    this.errorMessage = null;
    request(DeleteBlogPost, { id: this.id })
      .then(() => this.$router.push('/blog'))
      .catch(e => this.displayErrors(e))
      .then(() => (this.loading = null));
  }

  public onHeroChanged(event: any) {
    this.editHero = event.target.files[0];
  }

  public onThumbnailChanged(event: any) {
    this.editThumbnail = event.target.files[0];
  }

  public updateReadTime() {
    const post = this.$refs.post as any;
    if (!post) {
      this.readTime = '∞ min';
    } else {
      const text = post.innerText;
      const count = text.trim().split(/\s+/).length;
      this.readTime = `${Math.round(count / 200) || 1} min`;
    }
  }

  public formatDate(date: Date | string) {
    return new Date(date).toLocaleDateString(undefined, {
      day: '2-digit',
      year: 'numeric',
      month: 'long',
    });
  }

  private displayErrors(errors: string | string[] | any[]) {
    this.errorMessage = this.parseErrors(errors);
  }

  private parseErrors(errors: string | string[] | any[]) {
    const err = errors && errors[0];
    if (!err) {
      return 'Something went wrong. Please try again later.';
    }

    const msg = err.message || err;
    return typeof msg === 'string'
      ? msg
      : msg.error || 'Something went wrong. Please try again later.';
  }

  private async uploadPhotos(files: { hero?: any; thumbnail?: any }) {
    const formData = new FormData();
    if (files.hero) {
      formData.append('hero', files.hero);
    }
    if (files.thumbnail) {
      formData.append('thumbnail', files.thumbnail);
    }

    const uploadResult = await fetch(`${API_HOST}/images/blog/${this.id}`, {
      method: 'POST',
      body: formData,
      headers: {
        Authorization: getAuthHeader(),
      },
    });

    if (!uploadResult.ok) {
      throw new Error(uploadResult.statusText);
    }
  }

  private async updatePost(
    id: string,
    fields: { text?: string; name?: string; published?: boolean }
  ) {
    return request(UpdateBlogPost, { id, fields: JSON.stringify(fields) }).then(
      () => this.loadBlogPost()
    );
  }
}
